/**
 * Sentry browser error tracking.
 *
 * Two parts:
 *   1. CONFIGURATION — init, DSN, sampling, user context
 *   2. SECRET SCRUBBING — ensures no secrets leak into Sentry events
 *
 * The scrubbing is key-based (scrub fields named "password", "token", etc.)
 * plus lightweight token-format scrubbing for string fields (JWTs, Bearer
 * tokens, API key prefixes). Server-side ENV-value scrubbing is handled by
 * the Ruby initializer — the browser doesn't have access to those values.
 */

import * as Sentry from "@sentry/browser";

// ==========================================================================
// CONFIGURATION
// ==========================================================================

if (globalThis.Qwoted.SENTRY_DSN) {
  Sentry.init({
    dsn: globalThis.Qwoted.SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.1,

    beforeSend(event) {
      return scrubEvent(event); // eslint-disable-line no-use-before-define
    },
    beforeSendTransaction(event) {
      return scrubEvent(event); // eslint-disable-line no-use-before-define
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        breadcrumb.data = scrubObject(breadcrumb.data); // eslint-disable-line no-param-reassign, no-use-before-define
      }
      if (breadcrumb.message) {
        breadcrumb.message = scrubString(breadcrumb.message); // eslint-disable-line no-param-reassign, no-use-before-define
      }
      return breadcrumb;
    },
  });

  if (globalThis.Qwoted.CURRENT_USER_EMAIL) {
    Sentry.setUser({ email: globalThis.Qwoted.CURRENT_USER_EMAIL });
  }
}

// ==========================================================================
// SECRET SCRUBBING
//
// scrubEvent / scrubObject / scrubString are hoisted, so they're available
// to the hooks above even though they're defined below.
// ==========================================================================

/** Key-based: field names matching this pattern get replaced entirely. */
const SECRET_PATTERN = /passw|secret|token|_key|crypt|salt|certificate|otp|ssn|api.?key|authorization|cookie/i;
const FILTERED = "[FILTERED]";

/** Value-based: common token formats that might appear in error messages or URLs. */
const TOKEN_PATTERNS = [
  /Bearer\s+\S{8,}/gi,
  /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, // JWTs
  /\b(?:sk|pk|rk)_(?:live|test|prod)_[A-Za-z0-9]{8,}\b/g, // Stripe-style keys
  /\b(?:ghp|gho|ghu|ghs|ghr)_[A-Za-z0-9]{20,}\b/g, // GitHub PATs
  /\bSG\.[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]{20,}\b/g, // SendGrid keys
];

/** Replace known token formats in a plain string. */
function scrubString(str) {
  if (typeof str !== "string") return str;
  let result = str;
  TOKEN_PATTERNS.forEach((pattern) => {
    result = result.replace(pattern, FILTERED);
  });
  return result;
}

/** Recursively scrub an object's keys that match SECRET_PATTERN. */
function scrubObject(obj) {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map((item) => scrubObject(item));
  const result = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (SECRET_PATTERN.test(key)) {
      result[key] = FILTERED;
    } else if (typeof value === "object" && value !== null) {
      result[key] = scrubObject(value);
    } else {
      result[key] = value;
    }
  });
  return result;
}

/** Scrub all first-class event fields (errors and transactions). */
function scrubEvent(event) {
  if (event.message) event.message = scrubString(event.message); // eslint-disable-line no-param-reassign
  if (event.transaction) event.transaction = scrubString(event.transaction); // eslint-disable-line no-param-reassign

  if (event.request) {
    if (event.request.headers) event.request.headers = scrubObject(event.request.headers); // eslint-disable-line no-param-reassign
    if (event.request.data && typeof event.request.data === "object") {
      event.request.data = scrubObject(event.request.data); // eslint-disable-line no-param-reassign
    }
    if (event.request.url) event.request.url = scrubString(event.request.url); // eslint-disable-line no-param-reassign
    if (event.request.query_string) event.request.query_string = scrubString(event.request.query_string); // eslint-disable-line no-param-reassign
  }
  if (event.breadcrumbs) {
    event.breadcrumbs.forEach((bc) => {
      if (bc.data) bc.data = scrubObject(bc.data); // eslint-disable-line no-param-reassign
      if (bc.message) bc.message = scrubString(bc.message); // eslint-disable-line no-param-reassign
    });
  }
  if (event.extra) event.extra = scrubObject(event.extra); // eslint-disable-line no-param-reassign
  if (event.contexts) event.contexts = scrubObject(event.contexts); // eslint-disable-line no-param-reassign
  if (event.tags) event.tags = scrubObject(event.tags); // eslint-disable-line no-param-reassign
  return event;
}
